import { useLanguage } from "@tlc/ls2pac/components/Language";
import React, { useMemo } from "react";
import PrintDialog from "../PrintDialog";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";
import { ListService, ListType } from "../ListService";
import { ListSortType, SearchSortType } from "./SavedListSortControl";

interface SavedListBibliographyPrintDialogProps {
  listId: number;
  listType: ListType;
  total: number;
  disabled?: boolean;
}

function getLocation(resource: ResourceDWR): string {
  if (resource.publicationInformations.length === 0) return "";
  const location = resource.publicationInformations[0].publisherName.trim();
  if (location.length > 0 && !location.match(/,$/)) {
    return `${location},`;
  }
  return location;
}

function getDate(resource: ResourceDWR): string {
  if (resource.publicationInformations.length === 0) return "";
  const date = resource.publicationInformations[0].publicationDate.trim();
  if (date.length > 0 && !date.match(/.$/)) {
    return `${date}.`;
  }
  return date;
}

function getPlace(resource: ResourceDWR): string {
  if (resource.publicationInformations.length === 0) return "";
  const place = resource.publicationInformations[0].publicationPlace.trim();
  if (place.length > 0 && !place.match(/:$/)) {
    return `${place}:`;
  }
  return place;
}

function getPages(resource: ResourceDWR): string {
  return resource.extent;
}

function getIssue(resource: ResourceDWR): string {
  return ""; // NOT CURRENTLY SUPPORTED
}

function getTitle(resource: ResourceDWR): string {
  let shortTitle = resource.shortTitle.trim();
  if (shortTitle.length > 0 && !shortTitle.match(/\.$/)) {
    shortTitle += ".";
  }
  return shortTitle;
}

function getAuthor(resource: ResourceDWR): string {
  var shortAuthor = resource.shortAuthor.trim();

  if (shortAuthor.length > 0 && !shortAuthor.match(/\.$/)) {
    shortAuthor += ".";
  }
  return shortAuthor;
}

function getFormat(resource: ResourceDWR): string {
  return `${resource.format}.`;
}

function getURL(resource: ResourceDWR): string {
  if (resource.rtype !== 2) {
    return "";
  }
  return `<${resource.link}>`;
}

function getGroup(resource: ResourceDWR): string {
  if (resource.rtype !== 2) {
    return "";
  }
  return resource.rssFeedName;
}

enum BibliographicFormat {
  book = "book",
  large_print = "large print",
  book_on_tape = "book on tape",
  book_on_cd = "book on cd",
  ebook = "ebook",
  eaudiobook = "eaudiobook",
  braille = "braille",
  audiobook = "audiobook",
  video = "video",
  dvd = "dvd",
  visual_materials = "visual materials",
  evideo = "evideo",
  vhs = "vhs",
  video_recording = "video recording",
  videogame = "videogame / cdrom",
  bluray = "blu-ray",
  sound_recording = "sound_recording",
  music = "music",
  emusic = "emusic",
  music_cd = "music_cd",
  cassette = "cassette",
  compact_disc = "compact disc",
  score = "score",
  printed_music = "printed music",
  journal = "journal",
  ejournal = "ejournal",
  rss = "rss",
  newspaper = "newspaper",
  magazine = "magazine",
  serial = "serial",
  picture = "picture",
  photograph = "photograph",
  painting = "painting",
  drawing = "drawing",
  print = "print",
  slide = "slide",
  microfiche = "microfiche",
  microfilm = "microfilm",
  map = "map",
  atlas = "atlas",
  cartographic_materials = "cartographic materials",
  globe = "globe",
  database = "database",
  website = "website",
  unknown = "unknown",
  kit = "kit",
  cdrom = "cdrom",
  software = "software",
  toy = "toy",
  object = "object",
  projected = "projected",
  nonprojected = "non-projected",
  electronic_resource = "electronic resource",
  eresource = "eresource",
  continuing_resources = "continuing resources",
  archival = "archival / mixed materials",
}

function getBibliographicTypeFromFormat(format: string) {
  const type = format.toLowerCase();
  if (
    Object.values(BibliographicFormat).includes(type as BibliographicFormat)
  ) {
    return type;
  }
  return null;
}

export function getBibliographicType(resource: ResourceDWR): string {
  const { format = "", imageDisplays = [], rtype } = resource;
  const RSS_TYPE = 2;
  const typeFromFormat = getBibliographicTypeFromFormat(format ?? "");
  if (typeFromFormat) {
    return typeFromFormat;
  }

  for (const display of imageDisplays) {
    // naturally sorted most specific to least specific
    const type = getBibliographicTypeFromFormat(display.imageType);
    if (type) {
      return type;
    }
  }

  return rtype === RSS_TYPE
    ? BibliographicFormat.rss
    : BibliographicFormat.unknown;
}

export function getBibliographyTemplate(resource: ResourceDWR) {
  // TODO sorting
  const type = getBibliographicType(resource);
  const author = getAuthor(resource);
  const title = getTitle(resource);

  // the title without A, An, or The at the beginning for MLA sorting
  const sortTitle = title.replace(/^(A |An |The )/i, "");

  switch (type) {
    case BibliographicFormat.book:
    case BibliographicFormat.large_print:
    case BibliographicFormat.book_on_tape:
    case BibliographicFormat.book_on_cd:
    case BibliographicFormat.ebook:
    case BibliographicFormat.eaudiobook:
    case BibliographicFormat.braille:
    case BibliographicFormat.audiobook:
      return {
        sortKey: author.length > 0 ? author : sortTitle,
        html: `<p class="citation"><span class="author">${author}</span>
		<span class="title" style="text-decoration:underline;">${title}</span>
		<span class="publication">
			<span class="place">${getPlace(resource)}</span>
			<span class="location">${getLocation(resource)}</span>
			<span class="date">${getDate(resource)}</span>
		</span></p>`,
      };
    case BibliographicFormat.video:
    case BibliographicFormat.dvd:
    case BibliographicFormat.visual_materials:
    case BibliographicFormat.evideo:
    case BibliographicFormat.vhs:
    case BibliographicFormat.video_recording:
    case BibliographicFormat.videogame:
    case BibliographicFormat.bluray:
      return {
        sortKey: title.length === 0 ? author : sortTitle,
        html: `<p class="citation"><span class="title" style="text-decoration:underline;">${title}</span>
		<span class="author" alt="director">${author}</span>
		<span class="publication">
			<span class="format">${getFormat(resource)}</span>
			<span class="location">${getLocation(resource)}</span>
			<span class="date">${getDate(resource)}</span>
		</span></p>`,
      };
    case BibliographicFormat.sound_recording:
    case BibliographicFormat.music:
    case BibliographicFormat.emusic:
    case BibliographicFormat.music_cd:
    case BibliographicFormat.cassette:
    case BibliographicFormat.compact_disc:
    case BibliographicFormat.score:
    case BibliographicFormat.printed_music:
      return {
        sortKey: author.length > 0 ? author : sortTitle,
        html: `<p class="citation"><span class="author" alt="artist,composer">${author}</span>
		<span class="title" quoted="true">${title}</span>
		<span class="group" style="text-decoration:underline;" alt="album" >${getGroup(
      resource,
    )}</span>
		<span class="publication">
			<span class="place">${getPlace(resource)}</span>
			<span class="date">${getDate(resource)}</span>
			<span class="pages">${getPages(resource)}</span>
		</span></p>`,
      };
    case BibliographicFormat.journal:
    case BibliographicFormat.ejournal:
      return {
        sortKey: author.length > 0 ? author : sortTitle,
        html: `<p class="citation"><span class="author">${author}</span>
		<span class="title" quoted="true">${title}</span>
		<span class="group" style="text-decoration:underline;" alt="journal">${getGroup(
      resource,
    )}</span>
		<span class="publication">
			<span class="issue">${getIssue(resource)}</span>
			<span class="date">${getDate(resource)}</span>
			<span class="pages">${getPages(resource)}</span>
		</span></p>`,
      };
    case BibliographicFormat.newspaper:
    case BibliographicFormat.magazine:
    case BibliographicFormat.serial:
      return {
        sortKey: author.length > 0 ? author : sortTitle,
        html: `<p class="citation"><span class="author">${author}</span>
		<span class="title" quoted="true">${title}</span>
		<span class="group" style="text-decoration:underline;" alt="periodical">${getGroup(
      resource,
    )}</span>
		<span class="publication">
			<span class="date">${getDate(resource)}</span>
			<span class="pages">${getPages(resource)}</span>
		</span></p>`,
      };
    case BibliographicFormat.picture:
    case BibliographicFormat.photograph:
    case BibliographicFormat.painting:
    case BibliographicFormat.drawing:
    case BibliographicFormat.print:
    case BibliographicFormat.slide:
      return {
        sortKey: author.length > 0 ? author : sortTitle,
        html: `<p class="citation"><span class="author" alt="artist, photographer">${author}</span>
		<span class="title" style="text-decoration:underline;">${title}</span>
		<span class="publication">
			<span class="date">${getDate(resource)}</span>
			<span class="location">${getLocation(resource)}</span>
		</span></p>`,
      };
    case BibliographicFormat.microfiche:
    case BibliographicFormat.microfilm:
      return {
        sortKey: author.length > 0 ? author : sortTitle,
        html: `<p class="citation"><span class="author">${author}</span>
		<span class="title" quoted="true">${title}</span>
		<span class="group" style="text-decoration:underline;" alt="periodical">${getGroup(
      resource,
    )}</span>
		<span class="publication">
			<span class="issue">${getIssue(resource)}</span>
			<span class="date">${getDate(resource)}</span>
			<span class="pages">${getPages(resource)}</span>
		</span></p>`,
      };
    case BibliographicFormat.map:
    case BibliographicFormat.atlas:
    case BibliographicFormat.cartographic_materials:
    case BibliographicFormat.globe:
      return {
        sortKey: sortTitle,
        html: `<p class="citation"><span class="title" style="text-decoration:underline;">${title}</span>
		<span class="group">${getGroup(resource)}</span>
		<span class="publication">
			<span class="place">${getPlace(resource)}</span>
			<span class="location">${getLocation(resource)}</span>
			<span class="date">${getDate(resource)}</span>
		</span></p>`,
      };
    case BibliographicFormat.rss:
      return {
        sortKey: author.length > 0 ? author : sortTitle,
        html: `<p class="citation"><span class="author">${author}</span>
		<span class="title" quoted="true">${title}</span>
		<span class="group" style="text-decoration:underline;" alt="website">${getGroup(
      resource,
    )}</span>
		<span class="url">${getURL(resource)}</span></p>`,
      };

    case BibliographicFormat.database:
    case BibliographicFormat.website:
      return {
        sortKey: author.length > 0 ? author : sortTitle,
        html: `<p class="citation"><span class="author">${author}</span>
		<span class="title" quoted="true">${title}</span>
		<span class="publisher"/>
		<span class="date">${getDate(resource)}</p>`,
      };
    default:
      return {
        sortKey: author.length > 0 ? author : sortTitle,
        html: `<p class="citation"><span class="author">${author}</span>
		<span class="title" quoted="true">${title}</span></p>`,
      };
  }
}

export default function SavedListBibliographyPrintDialog({
  listId,
  listType,
  total,
  disabled = false,
}: SavedListBibliographyPrintDialogProps) {
  const { translate } = useLanguage();
  const [printResources, setPrintResources] = React.useState<ResourceDWR[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasFetchedAllResources, setHasFetchedAllResources] =
    React.useState(false);

  // Reset fetch state if user switches lists or removes an item from the list
  React.useEffect(() => {
    setHasFetchedAllResources(false);
    setPrintResources([]);
  }, [listId, total]);

  const handleFetchForPrint = async () => {
    if (hasFetchedAllResources || total === 0) return;

    setIsLoading(true);
    try {
      // Determine the default sort type based on the list type to satisfy the getAllListResources API. Will be re-sorted to MLA alphabetical in buildHtml
      const apiSort =
        listType === ListType.Prohibited
          ? SearchSortType.Title
          : ListSortType.Title;

      const { resources } = await ListService.getAllListResources(
        listId,
        listType,
        total,
        apiSort,
      );

      setPrintResources(resources);
      setHasFetchedAllResources(true);
    } catch (error) {
      console.error("Failed to fetch resources for bibliography:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const htmlContent = useMemo(() => {
    const normalResources = printResources?.filter((r) => r.rtype === 1); // Legacy code filtered on rtype == 1

    let citationsHtmlContent = "";
    if (normalResources.length > 0) {
      citationsHtmlContent = normalResources
        .slice()
        .filter((r) => r.rtype == 1)
        .map((r) => getBibliographyTemplate(r))
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey)) // enforce MLA alphabetic sorting
        .map((t) => t.html)
        .join("\n");
    } else if (!isLoading && hasFetchedAllResources) {
      citationsHtmlContent = `<div>${translate("noItemsToPrint")}</div>`;
    }

    return `
    <div>
      <h2 align="center">${translate("bibliography")}</h2>
      <div id="citations">${citationsHtmlContent}</div>
      <div style="border-top: 1px solid #ccc; padding-top: 10px; margin-top: 20px;">
        ${translate("format")}: MLA
      </div>
    </div>
  `;
  }, [printResources, translate, isLoading, hasFetchedAllResources]);

  return (
    <PrintDialog
      buttonText={translate("viewBibliography")}
      dialogTitle={translate("bibliography")}
      htmlContent={htmlContent}
      disabled={disabled}
      isLoading={isLoading}
      onOpenButtonClick={handleFetchForPrint}
    />
  );
}
